import React, { useRef, useState } from 'react';
import { Link } from "react-router-dom";
import Banner from '../components/Banner';
import Breadcrumb from '../components/Breadcrumb';
import { motion } from 'framer-motion';
import { useNavigate } from "react-router-dom";
import SEO from '../components/SEO';
import ServiceBg from '../images/service-bg.png'
import staffing from "../images/services/it-staff.jpg";
import training from "../images/services/training-program.jpg";
import career from "../images/services/career-dev.jpg";
import cloud from "../images/services/cloud-solutions.jpg";

import '../styles/pages/Services.css';

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import { useScroll, useTransform, useMotionValueEvent } from "framer-motion";

import {
    FaArrowRight,
    FaUsers,
    FaChalkboardTeacher,
    FaHandsHelping
} from "react-icons/fa";
import Newsection from '../components/Newsection';

import Google from "../images/Logo-Google-Cloud.png";
import Slack from "../images/openstack-logo.png";
import Heroku from "../images/aws_logo.png";
import Agworld from "../images/heroku_logo.png";
import Riotinto from "../images/digital_ocean_logo.png";

const brands = [Google, Slack, Heroku, Agworld, Riotinto];


const Services = () => {
    const fadeLeft = {
        hidden: { opacity: 0, x: -80 },
        show: {
            opacity: 1,
            x: 0,
            transition: { duration: 0.8, ease: "easeOut" }
        }
    };

    const fadeRight = {
        hidden: { opacity: 0, x: 80 },
        show: {
            opacity: 1,
            x: 0,
            transition: { duration: 0.8, ease: "easeOut" }
        }
    };

    const itemAnim = {
        hidden: { opacity: 0, y: 20 },
        show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
    };

    const navigate = useNavigate();

    /* ================= SERVICES DATA ================= */

    const services = [
        {
            title: "IT STAFFING",
            desc: "Connecting organizations with skilled IT professionals to meet project and business needs.",
            image: staffing,
            link: "/services/it-staffing"
        },
        {
            title: "TRAINING PROGRAMS",
            desc: "Providing industry-relevant training to enhance IT workforce readiness.",
            image: training,
            link: "/services/training-programs"
        },
        {
            title: "CAREER DEVELOPMENT",
            desc: "Guiding individuals to achieve their career goals in the IT industry.",
            image: career,
            link: "/services/career-development"
        },
        {
            title: "CLOUD SOLUTIONS",
            desc: "Providing scalable and secure cloud solutions to help businesses improve efficiency and digital transformation.",
            image: cloud,
            link: "/services/cloud-solutions"
        }
    ];

    const processData = [
        {
            step: "01",
            title: "Understanding Requirements",
            desc: "We analyze client needs and project goals to design the right IT solution."
        },
        {
            step: "02",
            title: "Strategic Planning",
            desc: "Our team creates a structured plan to deliver efficient and scalable solutions."
        },
        {
            step: "03",
            title: "Implementation",
            desc: "We deploy the right professionals and technologies to execute the plan."
        },
        {
            step: "04",
            title: "Support & Optimization",
            desc: "Continuous support and improvements ensure long-term success."
        }
    ];

    // ────────────────────────────────────────────────
    // WORKING PROCESS LOGIC
    // ────────────────────────────────────────────────

    const processRef = useRef(null);
    const [stepNumber, setStepNumber] = useState(1);

    const { scrollYProgress } = useScroll({
        target: processRef,
        offset: ["start start", "end start"],
    });

    // Position moves in 4 clear steps – last value stays at 300%
    const moveX = useTransform(
        scrollYProgress,
        [0, 0.20, 0.45, 0.70, 1.0],
        ["0%", "100%", "200%", "300%", "300%"]
    );

    // Number changes EARLIER → feels like it's "announcing" the step
    useMotionValueEvent(scrollYProgress, "change", (latest) => {
        if (latest < 0.20) setStepNumber(1);
        else if (latest < 0.45) setStepNumber(2);
        else if (latest < 0.70) setStepNumber(3);
        else setStepNumber(4);
    });

    /* Fade Animation */
    const fadeUp = {
        hidden: { opacity: 0, y: 50 },
        show: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6 }
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
        >

            <SEO
                title="Services"
                description="Our Services - Fortune Cloud Solutions"
            />

            <Banner
                title="OUR SERVICES"
                image={ServiceBg}
            />

            <section className="service-main py-4">

                <div className="container">
                    <Breadcrumb />
                </div>

                {/* ================= SERVICES SECTION ================= */}
                <section className="services-section">
                    <div className="container">

                        <motion.div
                            className="row align-items-center services-top"
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true }}
                        >
                            <div className="col-lg-6">
                                <span className="industries-badge">
                                    WHAT WE OFFER
                                </span>
                                <motion.h2 variants={fadeUp}>
                                    TAILORED IT SOLUTIONS TO – <br />
                                    <span>DRIVE YOUR SUCCESS.</span>
                                </motion.h2>
                            </div>

                            <div className="col-lg-6">
                                <motion.p variants={fadeUp}>
                                    Fortune Cloud Solutions provides tailored IT staffing,
                                    industry-relevant training and career development services,
                                    empowering businesses and professionals to thrive.
                                </motion.p>
                            </div>
                        </motion.div>

                        {/* ===== SWIPER ===== */}
                        <motion.div
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true }}
                            variants={fadeUp}
                        >
                            <Swiper
                                modules={[Navigation, Pagination, Autoplay]}
                                spaceBetween={30}
                                loop
                                speed={1200}
                                autoplay={{
                                    delay: 3000,
                                    disableOnInteraction: false,
                                    pauseOnMouseEnter: true
                                }}
                                navigation
                                pagination={{ clickable: true }}
                                breakpoints={{
                                    320: { slidesPerView: 1.5 },
                                    768: { slidesPerView: 2 },
                                    1024: { slidesPerView: 3 },
                                    1400: { slidesPerView: 3 }
                                }}
                                className="services-swiper"
                            >
                                {services.map((item, i) => (
                                    <SwiperSlide key={i}>
                                        <div
                                            className="services-card h-100"
                                            onClick={() => navigate(item.link)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="services-img">
                                                <img src={item.image} alt={item.title} />
                                                <div className="red-bars" />
                                            </div>

                                            <div className="services-content">
                                                <h4>{item.title}</h4>
                                                <p>{item.desc}</p>
                                            </div>
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </motion.div>

                    </div>
                </section>

                {/* ================= WORKING PROCESS ================= */}
                <section className="process-section" ref={processRef}>

                    {/* ===== PROCESS SECTION ===== */}
                    <section className="news-section">
                        <div className="container">

                            <div className="news-header">
                                <div className="header-left">
                                    <span className="testmonial-badge">
                                        OUR PROCESS
                                    </span>

                                    <h2 className='text-uppercase'>
                                        How we work for<br />
                                        <span className="purple-text">our valued customers</span>
                                    </h2>

                                </div>
                                {/* <div className="header-right">
                                    <Link to="/about">
                                        <motion.button className="discover-btn" variants={itemAnim}>
                                            DISCOVER MORE →
                                        </motion.button>
                                    </Link>
                                </div> */}
                            </div>

                            <div className="process-container">
                                {processData.map((item, index) => (
                                    <div className="process-item" key={index}>

                                        <div className="process-card">
                                            <span className="step-number border">{item.step}</span>
                                            <h3 className="border-bottom pb-2 fw-bold">{item.title}</h3>

                                            <p>{item.desc}</p>
                                        </div>

                                        {index !== processData.length - 1 && (
                                            <div className="process-arrow">
                                                <span><FaArrowRight className="process-arrow-icon fs-6" /></span>
                                            </div>
                                        )}

                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                </section>

                {/* Contact Main */}
                <section className=" ">
                    <div className="container ">
                        <div className="row g-0 align-items-center">

                            {/* LEFT MAP */}
                            <motion.div
                                className="col-lg-6 map-wrapper"
                                variants={fadeLeft}
                                initial="hidden"
                                whileInView="show"
                                viewport={{ once: true, margin: "-80px" }}
                            >
                                <iframe
                                    title="Google Map"
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3343.1637896731727!2d-96.8199016!3d33.0784753!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864c3cb15959334d%3A0x1f7d18088d9ed2ca!2s5717%20Legacy%20Dr%20Suite%20250%2C%20Plano%2C%20TX%2075024%2C%20USA!5e0!3m2!1sen!2sin!4v1776404959499!5m2!1sen!2sin"
                                    width="100%"
                                    height="600"
                                    style={{ border: 0 }}
                                    allowFullScreen=""
                                    loading="lazy"
                                ></iframe>
                            </motion.div>

                            {/* RIGHT FORM */}
                            <motion.div
                                className="col-lg-6 form-wrapper"
                                variants={fadeRight}
                                initial="hidden"
                                whileInView="show"
                                viewport={{ once: true, margin: "-80px" }}
                            >
                                <div className="form-inner">

                                    <div className="section-heading">
                                        <span className="heading-line"></span>
                                        <h2>GET IN TOUCH</h2>
                                    </div>

                                    <form className="contact-form">

                                        <div className="row g-4">
                                            <div className="col-md-6">
                                                <input type="text" placeholder="NAME" />
                                            </div>

                                            <div className="col-md-6">
                                                <input type="email" placeholder="E-MAIL" />
                                            </div>

                                            <div className="col-md-6">
                                                <input type="text" placeholder="PHONE NUMBER" />
                                            </div>

                                            <div className="col-md-6">
                                                <input type="text" placeholder="YOUR WEBSITE" />
                                            </div>

                                            <div className="col-12">
                                                <textarea rows="5" placeholder="YOUR MESSAGE HERE"></textarea>
                                            </div>

                                            <div className="col-12">
                                                <button type="submit" className="submit-btn">
                                                    SUBMIT NOW
                                                </button>
                                            </div>
                                        </div>

                                    </form>

                                </div>
                            </motion.div>

                        </div>
                    </div>
                </section>

                {/* SECTION 2: PARTNERS BAR */}

                {/* <div className="brand-wrapper py-5 container">
                    <motion.div
                        className="brand-track"
                        animate={{ x: [0, "-50%"] }}
                        transition={{
                            duration: 20,
                            ease: "linear",
                            repeat: Infinity,
                        }}
                    >
                        {[...brands, ...brands].map((logo, i) => (
                            <div key={i} className="brand-item">
                                <img src={logo} alt="brand" />
                            </div>
                        ))}
                    </motion.div>
                </div> */}

            </section>

        </motion.div>
    );
};

export default Services;